import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import './Carousel.scss';
import { fetchBanners } from 'services/bannerService';
import { LeftOutlined, RightOutlined } from '@ant-design/icons';


interface CarouselProps {
  autoplay?: boolean;
  autoplaySpeed?: number; // in milliseconds
  showDots?: boolean;
  className?: string;
}

const Carousel: React.FC<CarouselProps> = ({
  autoplay = true,
  autoplaySpeed = 3000,
  showDots = true,
  className = '',
}) => {
  const { i18n } = useTranslation();
  const [currentIndex, setCurrentIndex] = useState(0);
  const [images, setImages] = useState<string[]>([]);
  const timerRef = useRef<NodeJS.Timeout | null>(null);

  // 重置自动播放计时器
  const resetAutoplayTimer = useCallback(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
    }

    if (!autoplay || images.length <= 1) return;

    timerRef.current = setInterval(() => {
      setCurrentIndex((prevIndex) => (prevIndex + 1) % images.length);
    }, autoplaySpeed);
  }, [autoplay, autoplaySpeed, images.length]);

  useEffect(() => {
    const getBanners = async () => {
      try {
        const response = await fetchBanners(i18n.language);
        const bannerList = response?.results || [];
        const currentLang = i18n.language.startsWith('zh') ? 'zh' : 'en';

        const imageUrls = bannerList
          .map((item: any) => {
            const imageObj = item?.bannerDesktop || item?.banner || item?.cover;

            if (imageObj && typeof imageObj === 'object') {
              return imageObj[currentLang] || imageObj['en'];
            }
            return typeof imageObj === 'string' ? imageObj : null;
          })
          .filter(Boolean);

          if (imageUrls.length > 0) {
          setImages(imageUrls);
        } else {
          console.warn('No banners found in API');
        }
      } catch (error) {
        console.error('Failed to fetch banners:', error);
      }
    };

    getBanners();
  }, [i18n.language]);

  const goToNext = useCallback(() => {
    setCurrentIndex((prevIndex) => (prevIndex + 1) % images.length);
    resetAutoplayTimer();
  }, [images.length, resetAutoplayTimer]);

  const goToPrevious = useCallback(() => {
    setCurrentIndex(
      (prevIndex) => (prevIndex - 1 + images.length) % images.length
    );
    resetAutoplayTimer();
  }, [images.length, resetAutoplayTimer]);

  const goToSlide = useCallback((index: number) => {
    setCurrentIndex(index);
    resetAutoplayTimer();
  }, [resetAutoplayTimer]);

  useEffect(() => {
    resetAutoplayTimer();

    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, [resetAutoplayTimer]);

  return (
    <div className={`custom-carousel ${className}`}>
      <div className="carousel-container">
        {/* Images */}
        <div className="carousel-slides">
          {images.map((image, index) => (
            <div
              key={index}
              className={`carousel-slide ${
                index === currentIndex ? 'active' : ''
              }`}
              style={{
                opacity: index === currentIndex ? 1 : 0,
                transition: 'opacity 0.5s ease-in-out',
              }}
            >
              <a
                href="https://www.voguelaundry.com/en/Whats-new.html"
                target="_blank"
                rel="noopener noreferrer"
              >
                <img src={image} />
              </a>
            </div>
          ))}
        </div>

        {/* Navigation Arrows - only show if more than 1 image */}
        {images.length > 1 && (
          <>
            <button
              className="carousel-arrow carousel-arrow-left"
              onClick={goToPrevious}
              aria-label="Previous slide"
            >
              <LeftOutlined></LeftOutlined>
            </button>
            <button
              className="carousel-arrow carousel-arrow-right"
              onClick={goToNext}
              aria-label="Next slide"
            >
              <RightOutlined></RightOutlined>
            </button>
          </>
        )}

        {/* Dots Indicator */}
        {showDots && images.length > 1 && (
          <div className="carousel-dots">
            {images.map((_, index) => (
              <button
                key={index}
                className={`carousel-dot ${
                  index === currentIndex ? 'active' : ''
                }`}
                onClick={() => goToSlide(index)}
                aria-label={`Go to slide ${index + 1}`}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default Carousel;
